/**
 * Cookie Consent Context
 * 
 * Manages global cookie consent state, settings modal visibility,
 * and banner display following the established context patterns
 */
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

import { 
  ConsentPreferences, 
  DEFAULT_CONSENT, 
  readConsentCookie, 
  writeConsentCookie 
} from '../utils/cookieUtils';

interface CookieConsentContextType {
  // Consent state
  consent: ConsentPreferences;
  
  // Modal and banner visibility
  showBanner: boolean;
  showSettings: boolean;
  
  // Actions
  setConsent: (category: keyof ConsentPreferences, value: boolean) => void;
  setAllConsent: (preferences: ConsentPreferences) => void;
  acceptAll: () => void;
  rejectAll: () => void;
  openSettings: () => void;
  closeSettings: () => void;
  hideBanner: () => void;
}

const CookieConsentContext = createContext<CookieConsentContextType | undefined>(undefined);

interface CookieConsentProviderProps {
  children: ReactNode;
}

/**
 * Cookie Consent Provider Component
 * 
 * Provides cookie consent state and management functions to the application
 */
export const CookieConsentProvider: React.FC<CookieConsentProviderProps> = ({ children }) => {
  // State for consent preferences
  const [consent, setConsentState] = useState<ConsentPreferences>(DEFAULT_CONSENT);
  
  // State for UI visibility
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  // Initialize consent state from cookie on mount
  useEffect(() => {
    const savedConsent = readConsentCookie();
    
    if (savedConsent) {
      // User has previously made a choice, use saved preferences
      setConsentState(savedConsent);
      setShowBanner(false);
    } else {
      // First visit, show banner
      setConsentState(DEFAULT_CONSENT);
      setShowBanner(true);
    }
  }, []);

  // Write consent to cookie whenever consent state changes
  useEffect(() => {
    // Only write to cookie if this is not the initial default state
    // This prevents writing unnecessary cookies on first load
    const savedConsent = readConsentCookie();
    if (savedConsent || consent !== DEFAULT_CONSENT) {
      writeConsentCookie(consent);
    }
  }, [consent]);

  /**
   * Set consent for a specific category
   */
  const setConsent = (category: keyof ConsentPreferences, value: boolean) => {
    setConsentState(prev => ({
      ...prev,
      [category]: category === 'necessary' ? true : value, // Necessary always true
    }));
  };

  /**
   * Set all consent preferences at once
   */
  const setAllConsent = (preferences: ConsentPreferences) => {
    setConsentState({
      ...preferences,
      necessary: true, // Ensure necessary is always true
    });
  };

  /**
   * Accept all cookies (enable all categories)
   */
  const acceptAll = () => {
    const allAccepted: ConsentPreferences = {
      necessary: true,
      preferences: true,
      analytics: true,
      marketing: true,
    };
    setConsentState(allAccepted);
    setShowBanner(false);
    setShowSettings(false);
  };

  /**
   * Reject all non-necessary cookies
   */
  const rejectAll = () => {
    setConsentState(DEFAULT_CONSENT);
    setShowBanner(false);
    setShowSettings(false);
  };

  /**
   * Open the cookie settings modal
   */
  const openSettings = () => {
    setShowSettings(true);
    // Don't hide banner automatically when opening settings
  };

  /**
   * Close the cookie settings modal
   */
  const closeSettings = () => {
    setShowSettings(false);
  };

  /**
   * Hide the banner (used when user makes a choice)
   */
  const hideBanner = () => {
    setShowBanner(false);
  };

  const contextValue: CookieConsentContextType = {
    consent,
    showBanner,
    showSettings,
    setConsent,
    setAllConsent,
    acceptAll,
    rejectAll,
    openSettings,
    closeSettings,
    hideBanner,
  };

  return (
    <CookieConsentContext.Provider value={contextValue}>
      {children}
    </CookieConsentContext.Provider>
  );
};

/**
 * Hook to access cookie consent context
 * 
 * @throws Error if used outside of CookieConsentProvider
 */
export const useCookieConsent = (): CookieConsentContextType => {
  const context = useContext(CookieConsentContext);
  
  if (context === undefined) {
    throw new Error('useCookieConsent must be used within a CookieConsentProvider');
  }
  
  return context;
};

export default CookieConsentProvider; 